import React, { useEffect, useState } from 'react'
import ExitUnsavedFormDialog from '../components/ExitUnsavedFormDialog'
import { ExitUnsavedFormsContext } from './ExitUnsavedFormsContext'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import type { FormSubmit } from './types'

interface Props {
  children: React.ReactElement
  hasSubmitOption?: boolean
}

interface Form<T> {
  isFormChanged: boolean
  onSubmit: FormSubmit<T>
  isValid: boolean
  values: T
}

/**
 * Provides a list of forms with their values, onSubmit function and information on whether the form is changed or not
 */
const ExitUnsavedFormsProvider = <T,>({ children, hasSubmitOption }: Props): React.ReactElement => {
  const { t } = useCustomTranslation()
  const [forms, setForms] = useState({})
  const [doChangesExist, setDoChangesExist] = useState(false)
  const [areChangesValid, setAreChangesValid] = useState(false)

  const addFormToUnsavedChangedCheck = (key: string, isFormChanged: boolean, isValid: boolean, onSubmit: FormSubmit<T>, values: T): void => {
    setForms(prev => ({
      ...prev,
      [key]: { isFormChanged, onSubmit, isValid, values },
    }))
  }

  const areChangesPresentWhileLeavingPage = (): boolean => {
    return Object.values<Form<T>>(forms).some((form: Form<T>) => form.isFormChanged)
  }

  const areChangesValidWhileLeavingPage = (): boolean => {
    return Object.values<Form<T>>(forms).every((form: Form<T>) => form.isValid)
  }

  const onSubmitAll = (): void => {
    Object.values<Form<T>>(forms).forEach(form => {
      if (form.isFormChanged) form.onSubmit(form.values)
    })
  }

  /** Clean up all forms on page leave. */
  const cleanUpForms = (): void => setForms({})

  useEffect(() => {
    setAreChangesValid(areChangesValidWhileLeavingPage())
    setDoChangesExist(areChangesPresentWhileLeavingPage())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forms])

  return (
    <ExitUnsavedFormsContext.Provider value={{ addFormToUnsavedChangedCheck }}>
      <ExitUnsavedFormDialog
        explanationText={t('common.dialogs.unsavedChanges.message')}
        onSubmit={onSubmitAll}
        isEnabled={doChangesExist}
        hasSubmitOption={hasSubmitOption}
        disableSubmit={!areChangesValid}
        cleanUpForms={cleanUpForms}
      />
      {children}
    </ExitUnsavedFormsContext.Provider>
  )
}
export default ExitUnsavedFormsProvider
